import React, {
  useState, useEffect, useRef, useMemo, forwardRef, useImperativeHandle, useCallback
} from 'react'
import { useGLTF, useAnimations } from '@react-three/drei'
import * as THREE from 'three'
import { cloneAndScaleUV, setupTextureRepeat } from "../../utils/geometryUltis"
import useDoorBoundingDataChecker from "../../utils/doorBoundingDataChecker"

const DoorSingle = forwardRef(({ door, onUpdate, textures, disableAnimation = false, gateTexture = null, ...props }, ref) => {
  const group = useRef()
  const { nodes, materials, animations } = useGLTF('/models/doors/Door-Single-v4.glb')
  const { mixer } = useAnimations(animations, group)

  const [isOpen, setIsOpen] = useState(false)

  // dwa materiały: zewnętrzny i wewnętrzny
  const [outerMat, setOuterMat] = useState(null)
  const [innerMat, setInnerMat] = useState(null)

  const isOpenRef = useRef(false)
  const mixerRef = useRef(mixer)
  const isAnimatingRef = useRef(false)

  const wallKey = useMemo(() => {
    const w = String(door?.wall || '').toLowerCase()
    if (w.includes('front')) return 'front'
    if (w.includes('back')) return 'back'
    if (w.includes('left')) return 'left'
    if (w.includes('right')) return 'right'
    return null
  }, [door?.wall])

  // ✅ kierunek otwierania zależny od door.opening
  // opening === 2 => flip (otwiera w "drugą stronę"), opening === 1 => jak było
  const flipHinge = useMemo(() => {
    return Number(door?.opening) === 2
  }, [door?.opening])

  // ✅ oś odbicia zależna od ściany (żeby zachować logikę po rotacji)
  const flipScale = useMemo(() => {
    if (!flipHinge) return [1, 1, 1]
    // ✅ flip zawsze po lokalnym Z (oś szerokości/zawiasu w tym modelu)
    return [1, 1, -1]
  }, [flipHinge])

  useImperativeHandle(ref, () => group.current)

  // === transform na scianie (Twoja logika) ===
  let positionOnTheWall = [0, 0, 0]
  let rotation = [0, 0, 0]
  let scale = [1, 1, 1]

  if (door.position && typeof door.position === 'object') {
    if (props.wall === 'front_wall') {
      positionOnTheWall = [door.position.x, door.position.y, -door.position.z]
      rotation = [0, 0, 0]
      scale = [1, 1, 1]
    } else if (props.wall === 'back_wall') {
      positionOnTheWall = [-door.position.x, door.position.y, -door.position.z]
      rotation = [0, 0, 0]
      scale = [-1, 1, -1]
    } else if (props.wall === 'left_wall') {
      positionOnTheWall = [door.position.x, door.position.y, door.position.z]
      rotation = [0, Math.PI / 2, 0]
      scale = [-1, 1, -1]
    } else if (props.wall === 'right_wall') {
      positionOnTheWall = [door.position.x, door.position.y, -door.position.z]
      rotation = [0, Math.PI / 2, 0]
      scale = [1, 1, 1]
    }
  }

  // === animacje (skalowanie pozycji) ===
  const scaledAnimations = useMemo(() => {
    const s = new THREE.Vector3(1, door.height / 100, door.width / 100)

    return (animations || []).map((clip) => {
      const scaledClip = clip.clone()
      scaledClip.tracks.forEach((track) => {
        if (track.name.endsWith('.position')) {
          for (let i = 0; i < track.values.length; i += 3) {
            track.values[i] *= s.x
            track.values[i + 1] *= s.y
            track.values[i + 2] *= s.z
          }
        }
      })
      return scaledClip
    })
  }, [animations, door.height, door.width, door.position])

  // ✅ helper: wymuś zamkniętą pozę (klatka 0)
  const freezeClosedPose = useCallback(() => {
    if (!mixerRef.current) return
    if (!group.current) return
    if (!scaledAnimations || scaledAnimations.length === 0) return

    const mixer = mixerRef.current
    mixer.stopAllAction()

    // ustaw 0 na wszystkich klipach
    scaledAnimations.forEach((clip) => {
      const action = mixer.clipAction(clip)
      action.reset()
      action.enabled = true
      action.clampWhenFinished = true
      action.setLoop(THREE.LoopOnce, 1)
      action.time = 0
      action.timeScale = 0
      action.paused = false
      action.play()
      action.paused = true
    })

    mixer.update(0)

    setIsOpen(false)
    isOpenRef.current = false
    isAnimatingRef.current = false
  }, [scaledAnimations])

  useEffect(() => {
    if (!group.current) return

    if (mixerRef.current) {
      mixerRef.current.stopAllAction()
      mixerRef.current.uncacheRoot(group.current)
    }

    mixerRef.current = new THREE.AnimationMixer(group.current)

    // przygotuj akcje
    scaledAnimations.forEach((clip) => {
      const action = mixerRef.current.clipAction(clip)
      action.clampWhenFinished = true
      action.enabled = true
      action.paused = true
      action.timeScale = 0
      action.time = 0
      action.play()
      action.paused = true
    })

    // jeśli animacje wyłączone -> zawsze zamknięte
    if (disableAnimation) freezeClosedPose()
  }, [scaledAnimations, disableAnimation, freezeClosedPose])

  // Animation loop
  useEffect(() => {
    // ✅ jak wyłączone animacje -> nie update’uj mixera
    if (disableAnimation) return

    let frameId
    const animate = () => {
      mixerRef.current?.update(1 / 60)
      frameId = requestAnimationFrame(animate)
    }
    frameId = requestAnimationFrame(animate)
    return () => cancelAnimationFrame(frameId)
  }, [disableAnimation])

  // gdy w locie ktoś włączy disableAnimation -> domknij
  useEffect(() => {
    if (!disableAnimation) return
    freezeClosedPose()
  }, [disableAnimation, freezeClosedPose])

  // === geometrie (bez zmian) ===
  const scaledDoorFrameGeometry = useMemo(
    () =>
      cloneAndScaleUV(
        nodes.Door_Single_Frame.geometry,
        1,
        door.height / 100,
        door.width / 100,
        door.width / 100,
        door.height / 100
      ),
    [nodes.Door_Single_Frame.geometry, door.height, door.width]
  )

  const scaledMesh012Geometry = useMemo(
    () =>
      cloneAndScaleUV(
        nodes.Mesh012.geometry,
        1,
        door.height / 100,
        door.width / 100,
        door.width / 100,
        door.height / 100
      ),
    [nodes.Mesh012.geometry, door.height, door.width]
  )

  const scaledMesh0121Geometry = useMemo(
    () =>
      cloneAndScaleUV(
        nodes.Mesh012_1.geometry,
        1,
        door.height / 100,
        door.width / 100,
        door.width / 100,
        door.height / 100
      ),
    [nodes.Mesh012_1.geometry, door.height, door.width]
  )

  // === repeat helper ===
  const applyRepeat = (tex, rx, ry) => {
    if (!tex) return
    setupTextureRepeat(tex, rx, ry)
  }

  const baseTemplate = useMemo(() => {
    return materials?.Material || materials?.['Material'] || new THREE.MeshStandardMaterial()
  }, [materials])

  const cloneBase = () => baseTemplate.clone()

  const getOutDirWorld = (wall) => {
    if (wall === 'front_wall') return new THREE.Vector3(0, 0, -1)
    if (wall === 'back_wall')  return new THREE.Vector3(0, 0,  1)
    if (wall === 'left_wall')  return new THREE.Vector3( 1, 0, 0)
    if (wall === 'right_wall') return new THREE.Vector3(-1, 0, 0)
    return new THREE.Vector3(0, 0, -1)
  }

  // === shader helper: discard po stronie (outer/inner) ===
  const attachSideDiscardShader = (mat, mode /* 'outer' | 'inner' */) => {
    mat.userData.uOutDir = getOutDirWorld(props.wall).normalize()

    mat.onBeforeCompile = (shader) => {
      shader.uniforms.uOutDir = { value: mat.userData.uOutDir }

      shader.vertexShader = shader.vertexShader.replace(
        'void main() {',
        `
          varying vec3 vWorldNormal;
          void main() {
        `
      )

      shader.vertexShader = shader.vertexShader.replace(
        '#include <begin_vertex>',
        `
          #include <begin_vertex>
          mat3 nm = transpose(inverse(mat3(modelMatrix)));
          vWorldNormal = normalize(nm * normal);
        `
      )

      shader.fragmentShader = shader.fragmentShader.replace(
        'void main() {',
        `
          varying vec3 vWorldNormal;
          uniform vec3 uOutDir;
          void main() {
            float dSide = dot(normalize(vWorldNormal), normalize(uOutDir));
            ${mode === 'outer'
          ? 'if (dSide <= 0.0) discard;'
          : 'if (dSide > 0.0) discard;'
        }
        `
      )

      mat.userData.shader = shader
    }

    mat.needsUpdate = true
  }

  useEffect(() => {
    if (!textures) return
    if (!wallKey) return
    if (!baseTemplate) return

    const doorTex = textures?.[`doors_${wallKey}_color`]
    let wallsTex = textures?.walls_color
    const insideTex = textures?.walls_inside_color

    if (gateTexture !== null) {
      wallsTex = gateTexture
    }

    if (!doorTex || !wallsTex) return

    const cloneTexLocal = (t) => (t ? (t.clone?.() ?? t) : null)
    const isWooden = (t) => (t?.materialType || t?.type) === "wooden"

    // ✅ przy flipie malujemy "inną stronę":
    // - normalnie: outer = drzwi, inner = inside
    // - po flipie: outer = inside, inner = drzwi
    const outerPaintIsDoor = !flipHinge

    // ===== OUTER =====
    const mOut = cloneBase()
    mOut.side = THREE.DoubleSide

    if (outerPaintIsDoor) {
      const doorIsWood = isWooden(doorTex)
      mOut.color = new THREE.Color(doorTex.hex ?? "#ffffff")

      const outTexSrc = doorIsWood ? doorTex : wallsTex

      mOut.map = cloneTexLocal(outTexSrc.textureDiff || outTexSrc.texture || null)
      mOut.metalnessMap = cloneTexLocal(outTexSrc.textureMetalness || null)

      mOut.metalness = doorTex.metalnessValue ?? 0
      mOut.roughness = doorTex.roughnessValue ?? 0.7

      // pressing z walls
      mOut.normalMap = cloneTexLocal(
        wallsTex.textureNormalness || wallsTex.textureNormal || wallsTex.textureNormalMap || null
      )

      if (mOut.map) {
        applyRepeat(
          mOut.map,
          1 / (outTexSrc.multiplierHorizontal || 1),
          1 / (outTexSrc.multiplierVertical || 1)
        )
      }
      if (mOut.metalnessMap) {
        applyRepeat(
          mOut.metalnessMap,
          1 / (outTexSrc.multiplierHorizontal || 1),
          1 / (outTexSrc.multiplierVertical || 1)
        )
      }
      if (mOut.normalMap) {
        applyRepeat(
          mOut.normalMap,
          1 / (wallsTex.multiplierHorizontal || 1),
          1 / (wallsTex.multiplierVertical || 1)
        )
      }
    } else {
      // outer wygląda jak środek
      if (insideTex) {
        mOut.color = new THREE.Color(insideTex.hex ?? "#f5f5f5")
        mOut.map = cloneTexLocal(insideTex.textureDiff || insideTex.texture || null)
        mOut.metalnessMap = cloneTexLocal(insideTex.textureMetalness || null)

        mOut.metalness = insideTex.metalnessValue ?? 0
        mOut.roughness = insideTex.roughnessValue ?? 1

        if (mOut.map) {
          applyRepeat(
            mOut.map,
            1 / (insideTex.multiplierHorizontal || 1),
            1 / (insideTex.multiplierVertical || 1)
          )
        }
        if (mOut.metalnessMap) {
          applyRepeat(
            mOut.metalnessMap,
            1 / (insideTex.multiplierHorizontal || 1),
            1 / (insideTex.multiplierVertical || 1)
          )
        }
      } else {
        mOut.map = null
        mOut.metalnessMap = null
        mOut.metalness = 0
        mOut.roughness = 1
        mOut.color = new THREE.Color("#f5f5f5")
      }

      // pressing nadal z walls
      mOut.normalMap = cloneTexLocal(
        wallsTex.textureNormalness || wallsTex.textureNormal || wallsTex.textureNormalMap || null
      )
      if (mOut.normalMap) {
        applyRepeat(
          mOut.normalMap,
          1 / (wallsTex.multiplierHorizontal || 1),
          1 / (wallsTex.multiplierVertical || 1)
        )
      }
    }

    attachSideDiscardShader(mOut, "outer")
    mOut.customProgramCacheKey = () => "door_side_discard_outer_v4_flip_paint"

    // ===== INNER =====
    const mIn = cloneBase()
    mIn.side = THREE.DoubleSide

    if (!outerPaintIsDoor) {
      const doorIsWood = isWooden(doorTex)
      mIn.color = new THREE.Color(doorTex.hex ?? "#ffffff")

      const inTexSrc = doorIsWood ? doorTex : wallsTex

      mIn.map = cloneTexLocal(inTexSrc.textureDiff || inTexSrc.texture || null)
      mIn.metalnessMap = cloneTexLocal(inTexSrc.textureMetalness || null)

      mIn.metalness = doorTex.metalnessValue ?? 0
      mIn.roughness = doorTex.roughnessValue ?? 0.7

      mIn.normalMap = cloneTexLocal(
        wallsTex.textureNormalness || wallsTex.textureNormal || wallsTex.textureNormalMap || null
      )

      if (mIn.map) {
        applyRepeat(
          mIn.map,
          1 / (inTexSrc.multiplierHorizontal || 1),
          1 / (inTexSrc.multiplierVertical || 1)
        )
      }
      if (mIn.metalnessMap) {
        applyRepeat(
          mIn.metalnessMap,
          1 / (inTexSrc.multiplierHorizontal || 1),
          1 / (inTexSrc.multiplierVertical || 1)
        )
      }
      if (mIn.normalMap) {
        applyRepeat(
          mIn.normalMap,
          1 / (wallsTex.multiplierHorizontal || 1),
          1 / (wallsTex.multiplierVertical || 1)
        )
      }
    } else {
      if (insideTex) {
        mIn.color = new THREE.Color(insideTex.hex ?? "#f5f5f5")
        mIn.map = cloneTexLocal(insideTex.textureDiff || insideTex.texture || null)
        mIn.metalnessMap = cloneTexLocal(insideTex.textureMetalness || null)

        mIn.metalness = insideTex.metalnessValue ?? 0
        mIn.roughness = insideTex.roughnessValue ?? 1

        if (mIn.map) {
          applyRepeat(
            mIn.map,
            1 / (insideTex.multiplierHorizontal || 1),
            1 / (insideTex.multiplierVertical || 1)
          )
        }
        if (mIn.metalnessMap) {
          applyRepeat(
            mIn.metalnessMap,
            1 / (insideTex.multiplierHorizontal || 1),
            1 / (insideTex.multiplierVertical || 1)
          )
        }
      } else {
        mIn.map = null
        mIn.metalnessMap = null
        mIn.metalness = 0
        mIn.roughness = 1
        mIn.color = new THREE.Color("#f5f5f5")
      }

      mIn.normalMap = cloneTexLocal(
        wallsTex.textureNormalness || wallsTex.textureNormal || wallsTex.textureNormalMap || null
      )
      if (mIn.normalMap) {
        applyRepeat(
          mIn.normalMap,
          1 / (wallsTex.multiplierHorizontal || 1),
          1 / (wallsTex.multiplierVertical || 1)
        )
      }
    }

    attachSideDiscardShader(mIn, "inner")
    mIn.customProgramCacheKey = () => "door_side_discard_inner_v4_flip_paint"

    setOuterMat(mOut)
    setInnerMat(mIn)
  }, [textures, wallKey, baseTemplate, props.wall, flipHinge])

  // === klik animacji (teraz respektuje disableAnimation) ===
  const handleClick = () => {
    if (disableAnimation) return
    if (!mixerRef.current || scaledAnimations.length === 0) return

    const clip = scaledAnimations[0]
    const action = mixerRef.current.clipAction(clip)
    action.reset()
    action.setLoop(THREE.LoopOnce, 1)
    action.clampWhenFinished = true
    action.enabled = true
    action.paused = false

    isAnimatingRef.current = true

    const onFinished = () => {
      isAnimatingRef.current = false
      mixerRef.current.removeEventListener('finished', onFinished)
    }

    mixerRef.current.addEventListener('finished', onFinished)

    if (!isOpen) {
      action.timeScale = 1
      action.play()
    } else {
      action.time = clip.duration
      action.timeScale = -1
      action.play()
    }

    setIsOpen(!isOpen)
  }

  // === bounding (bez zmian) ===
  useDoorBoundingDataChecker({
    nodes,
    door,
    onBoundingDataChanged: () => {
      if (isAnimatingRef.current) return

      const box = new THREE.Box3().setFromObject(group.current)
      const size = new THREE.Vector3()
      box.getSize(size)

      const updateData = { boundingData: { box, size } }

      // ✅ jeśli animacje wyłączone, zawsze traktuj jak "zamknięte"
      if (!isOpen || disableAnimation) {
        const [x = 0, y = 0, z = 0] = positionOnTheWall
        const adjustedY = y + door.height / 100 / 2

        let adjustedX = x
        let adjustedZ = z

        if (door.wall === 'back_wall') adjustedX += 0.5
        else if (door.wall === 'left_wall') adjustedZ -= 0.5
        else if (door.wall === 'right_wall') adjustedZ += 0.5
        else if (door.wall === 'front_wall') adjustedX -= 0.5

        updateData.closedBoundingBox = { box: box.clone(), size: size.clone() }
        updateData.positionAdjusted = [adjustedX, adjustedY, adjustedZ]

        onUpdate(updateData)
      }
    }
  })

  return (
    <group
      ref={group}
      dispose={null}
      position={positionOnTheWall}
      castShadow
      {...props}
      scale={scale}
      rotation={rotation}
      wall={props.wall}
    >
      {outerMat && innerMat ? (
        <group name="Scene" onClick={handleClick} scale={flipScale}>
          <mesh
            name="Door_Single_Frame"
            geometry={scaledDoorFrameGeometry}
            material={materials['Dark metal.001']}
          />

          <group
            name="Door_Single"
            position={[-0.498, (door.height / 100) * 0.511, (door.width / 100) * 0.486]}
          >
            {/* swap outer/inner przy flipie, bo normalne się odwracają */}
            <mesh
              name="Mesh012_outer"
              geometry={scaledMesh012Geometry}
              material={flipHinge ? innerMat : outerMat}
            />
            <mesh
              name="Mesh012_inner"
              geometry={scaledMesh012Geometry}
              material={flipHinge ? outerMat : innerMat}
            />

            <mesh
              name="Mesh012_1"
              geometry={scaledMesh0121Geometry}
              material={materials['Dark metal.001']}
            />
          </group>
        </group>
      ) : null}
    </group>
  )
})

export default DoorSingle
useGLTF.preload('/models/doors/Door-Single-v4.glb')
